/**
 * <countdown-timer> custom element.
 *
 * Two modes:
 *
 *  1) ROLLING mode (new): data-rolling-hours="12" data-anchor-tz="America/New_York"
 *     Counts down to the next N-hour boundary in the anchor timezone. When the
 *     timer hits zero, it silently starts the next cycle — never hides.
 *     All visitors worldwide see the same number at the same instant because
 *     the math is anchored to absolute UTC seconds with the anchor TZ offset
 *     applied. DST is handled live each tick via Intl.DateTimeFormat so the
 *     transition does not cause a sudden jump.
 *
 *  2) CUTOFF mode (legacy, still used by PDP ATC line): data-cutoff="2026-06-06T17:00:00-05:00"
 *     Counts down to a fixed ISO datetime. Hides its wrapper at zero.
 *
 * Mode is chosen by which attribute is present. data-rolling-hours wins if both
 * are present.
 */

class CountdownTimer extends HTMLElement {
  connectedCallback() {
    const rollingHours = parseInt(this.dataset.rollingHours || '', 10);
    if (rollingHours > 0) {
      this._mode = 'rolling';
      this._periodSec = rollingHours * 3600;
      this._anchorTz = this.dataset.anchorTz || 'America/New_York';
      // When remaining drops below this many seconds, jump to the next cycle
      // so the visitor always sees a comfortably-long window rather than a
      // sub-threshold "30 minutes left" panic. 0 disables the skip.
      const skipMin = parseInt(this.dataset.skipThresholdMin || '0', 10);
      this._skipThresholdSec = Math.max(0, skipMin) * 60;
      this._tick();
      this._interval = setInterval(() => this._tick(), 1000);
      return;
    }

    const cutoffStr = this.dataset.cutoff;
    if (cutoffStr) {
      this._mode = 'cutoff';
      this._target = new Date(cutoffStr).getTime();
      if (isNaN(this._target)) return;
      this._tick();
      this._interval = setInterval(() => this._tick(), 1000);
    }
  }

  disconnectedCallback() {
    clearInterval(this._interval);
  }

  /**
   * Returns the current UTC offset of the anchor timezone in seconds.
   * Positive east of UTC, negative west. Recomputed every tick so DST flips
   * are handled smoothly.
   */
  _anchorOffsetSec() {
    try {
      const parts = new Intl.DateTimeFormat('en-US', {
        timeZone: this._anchorTz,
        timeZoneName: 'shortOffset',
      }).formatToParts(new Date());
      const tzPart = parts.find((p) => p.type === 'timeZoneName');
      if (!tzPart) return 0;
      // e.g. "GMT-5" or "GMT-4:30" — strip the GMT prefix and parse.
      const match = tzPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
      if (!match) return 0;
      const sign = match[1] === '-' ? -1 : 1;
      const hours = parseInt(match[2], 10);
      const minutes = parseInt(match[3] || '0', 10);
      return sign * (hours * 3600 + minutes * 60);
    } catch {
      return 0;
    }
  }

  _tick() {
    let diffSec;

    if (this._mode === 'rolling') {
      const nowSec = Math.floor(Date.now() / 1000);
      const offsetSec = this._anchorOffsetSec();
      // Shift "now" into anchor-local seconds, then find how far we are into
      // the current N-hour window. remaining = period - (localNow mod period).
      const anchorNowSec = nowSec + offsetSec;
      const intoWindow = ((anchorNowSec % this._periodSec) + this._periodSec) % this._periodSec;
      diffSec = this._periodSec - intoWindow;
      // diffSec is always in (0, period]. When it would be 0, the modulo wraps
      // to period — so the timer never displays 00:00:00 and never hides.
      //
      // Skip-threshold: if remaining is under the threshold, jump to the next
      // cycle so visitors always see at least (period + 0) — never a stub
      // countdown. All visitors jump at the same moment because the threshold
      // check operates on the synchronized diffSec.
      if (this._skipThresholdSec > 0 && diffSec <= this._skipThresholdSec) {
        diffSec += this._periodSec;
      }
    } else if (this._mode === 'cutoff') {
      const diffMs = this._target - Date.now();
      if (diffMs <= 0) {
        this.closest('[data-countdown-wrap]')?.setAttribute('hidden', '');
        clearInterval(this._interval);
        return;
      }
      diffSec = Math.floor(diffMs / 1000);
    } else {
      return;
    }

    // For the rolling mode we render the full remaining seconds as a single
    // HH:MM:SS-style string even if it exceeds 24h (skip-threshold can push
    // diffSec up to ~periodSec + period). For cutoff mode we keep the legacy
    // "Xd Yh" formatting when the target is more than a day away.
    const totalHours = Math.floor(diffSec / 3600);
    const m = Math.floor((diffSec % 3600) / 60);
    const s = diffSec % 60;

    if (this._mode === 'rolling') {
      if (totalHours > 0) {
        const mm = String(m).padStart(2, '0');
        const ss = String(s).padStart(2, '0');
        this.textContent = `${totalHours}h ${mm}m ${ss}s`;
      } else {
        const ss = String(s).padStart(2, '0');
        this.textContent = `${m}m ${ss}s`;
      }
      return;
    }

    // cutoff mode — legacy formatting (days when > 24h, hours otherwise)
    const d = Math.floor(diffSec / 86400);
    const h = Math.floor((diffSec % 86400) / 3600);
    if (d > 0) {
      this.textContent = `${d}d ${h}h`;
    } else if (h > 0) {
      const mm = String(m).padStart(2, '0');
      const ss = String(s).padStart(2, '0');
      this.textContent = `${h}h ${mm}m ${ss}s`;
    } else {
      const ss = String(s).padStart(2, '0');
      this.textContent = `${m}m ${ss}s`;
    }
  }
}

customElements.define('countdown-timer', CountdownTimer);
